<template>
    <div>
        <DateRangeFilter v-model="dateRangeFilter" @input="resetAndReload" />
        <b-table id="inventory-history-table" :fields="fields" :items="historyItems"
            empty-text="No Inventory History" sort-direction="desc" sort-by="updated" no-provider-sorting show-empty
            :busy="loading" />
        <BottomPagerBar :value="pageNumber" :total-rows="totalCount" :per-page="pageSize" :loading="loading"
            :show-page-size-selector="true" :no-stick="true" :show-total="true"
            @input="onPageChange" @change-page-size="onPageSizeChange" />
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { BvTableFieldArray } from "bootstrap-vue";
import Axios from "axios";
import moment from "moment/moment";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { Store } from "@/models/Store";
import { Program } from "@/models/Program";
import { DrugProduct } from "@/models/Drug/DrugProduct";
import DateRangeFilter from "../DateRangeFilter.vue";
import BottomPagerBar from "../BottomPagerBar.vue";
import { getPageSizePreference, savePageSizePreference } from '@/util/pageSizePreference';

@Component({
    name: "InventoryHistory",
    components: { DateRangeFilter, BottomPagerBar },
})
export default class InventoryHistory extends Vue {
    @Prop() protected store?: Store;
    @Prop() protected program?: Program;
    @Prop() protected drug?: DrugProduct;

    protected historyItems: any[] = [];
    protected totalCount = 0;
    protected loading = false;

    private dateRangeFilter: any = { startDate: null, endDate: null };

    protected pageSize = 25;
    protected pageNumber = 1;

    mounted() {
        this.pageSize = getPageSizePreference(this, 'pageSize_InventoryHistory', 25);
        this.getInventoryHistory();
    }

    @Watch("pageSize")
    onPageSizePreferenceChanged() {
        savePageSizePreference(this, 'pageSize_InventoryHistory', this.pageSize);
    }

    // Paging is driven imperatively: each handler updates state, then fires exactly one fetch.
    // Changing the date range or page size resets back to page 1.
    resetAndReload() {
        this.pageNumber = 1;
        this.getInventoryHistory();
    }

    onPageChange(newPage: number) {
        this.pageNumber = newPage;
        this.getInventoryHistory();
    }

    onPageSizeChange(newSize: number) {
        this.pageSize = newSize;
        this.pageNumber = 1;
        this.getInventoryHistory();
    }

    get fields(): BvTableFieldArray {
        return [
            { key: "DrugName" },
            { key: "NDC11" },
            { key: "Updated", formatter: (value: any) => moment.utc(value).local().format("L LTS"), },
            { key: "UpdatedBy" },
            { key: "StoreName", label: "Store", },
            { key: "ProgramName", label: "Program", },
            { key: "LotNumber", label: "Lot #" },
            {
                key: "ExpirationDate",
                label: "Expiration",
                formatter: (value: any) => value ? moment.utc(value).format("L") : "",
            },
            {
                key: "Reason",
                label: "RxId / Reason",
                formatter: (value: any, _: any, item: any) => item.RxId ? item.RxId : value
            },
            { key: "OldQuantity" },
            { key: "NewQuantity", tdClass: this.cellClass },
        ];
    }

    get inventoryFilter() {
        return {
            storeId: this.store?.id,
            programId: this.program?.id,
            packageId: this.drug?.drugId,
            source: this.drug?.source,
            startDate: this.dateRangeFilter.startDate
                ? moment.utc(this.dateRangeFilter.startDate).startOf('day').toISOString() : null,
            endDate: this.dateRangeFilter.endDate
                ? moment.utc(this.dateRangeFilter.endDate).endOf('day').toISOString() : null,
            pageNumber: this.pageNumber,
            pageSize: this.pageSize,
        };
    }

    async getInventoryHistory() {
        this.loading = true;
        try {
            const resp = await Axios.get<{ items: any[]; totalCount: number }>(
                "/api/Inventory/History", { params: this.inventoryFilter });
            const histories = resp.data.items.map((value) => {
                if (!value.NewValue) return undefined;

                const newValue = JSON.parse(value.NewValue)[0];
                const ret: any = {
                    ...value,
                    Reason: newValue.Reason,
                    NewQuantity: newValue.Quantity,
                    LotNumber: newValue.LotNumber,
                    ExpirationDate: newValue.ExpirationDate,
                };

                if (value.OldValue) {
                    const oldValue = JSON.parse(value.OldValue)[0];
                    ret.OldQuantity = oldValue.Quantity;
                    ret.LotNumber = ret.LotNumber ?? oldValue.LotNumber;
                    ret.ExpirationDate = ret.ExpirationDate ?? oldValue.ExpirationDate;
                }
                return ret;
            });

            this.historyItems = histories.filter(h => h);
            this.totalCount = resp.data.totalCount;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.loading = false;
        }
    }

    cellClass(value: number, _key: string, item: any) {
        return this.cellClassForQuantities(value, item.OldQuantity);
    }

    cellClassForQuantities(newQuantity: number, oldQuantity: number): string {
        const qtyChange = Number(newQuantity - (oldQuantity ?? 0));
        if (qtyChange > 0) return "table-success";
        if (newQuantity < 0) return "table-danger";
        if (qtyChange < 0) return "table-warning";
        return "";
    }
};
</script>
